import { llm } from '@cotera/api';
import { JsonTypeBuilder } from '@cotera/client/app/components/json-type-builder';
import {
  useAction,
  useWatchableValue,
} from '@cotera/client/v0/actions/framework';
import { Button as NewButton } from '@cotera/client/v0/components/ui/button';
import { Combobox } from '@cotera/client/v0/components/ui/combobox';
import { Input } from '@cotera/client/v0/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@cotera/client/v0/components/ui/popover';
import { Slider } from '@cotera/client/v0/components/ui/slider';
import { CronExpression } from '@cotera/sdk/core';
import { Braces, Clock, Coins, SlidersHorizontal, Wrench } from 'lucide-react';
import { useColumnModel } from '../context';
import { ConfigSection } from './common';
import { Switch } from '@cotera/client/v0/components/ui/switch';
import {
  scheduleDescription,
  scheduleLabel,
  SENSIBLE_CRON_OPTIONS,
  STANDARD_SCHEDULES,
  StandardSchedule,
  valueFromSchedule,
  getScheduleContextFromEventStream,
} from '@cotera/client/v0/utils';
import { useWhoami } from '@cotera/client/app/stores/org';
import { useGatedModelFilter } from '@cotera/client/app/hooks/use-gated-models';
import { NewToolAction } from '@cotera/client/v0/tool/new-tool.action';

const OutputPopover: React.FC = () => {
  const model = useColumnModel();
  const { outputShape } = useWatchableValue(model.formState);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <NewButton size="icon" variant="ghost">
          <Braces />
        </NewButton>
      </PopoverTrigger>
      {/* <PopoverContent className="flex flex-col gap-4"> */}
      <PopoverContent asChild>
        <div className="flex flex-col gap-2 w-full z-50 max-h-[70vh] overflow-y-auto">
          <JsonTypeBuilder
            onChange={(shape) => {
              model.formState.set({
                ...model.formState.snapshot(),
                outputShape: shape,
              });
            }}
            value={outputShape}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};

const SchedulePopover: React.FC = () => {
  const model = useColumnModel();
  const { when } = useWatchableValue(model.formState);
  const org = useWhoami((s) => s.org);
  const eventStream = useWatchableValue(model.eventStream);

  // Get context from event stream (same logic as backend)
  const scheduleContext = eventStream
    ? getScheduleContextFromEventStream(org.id, eventStream)
    : undefined;

  const scheduleOptions = [
    { value: 'hourly', label: 'Hourly' },
    { value: 'daily', label: 'Daily' },
    { value: 'never', label: 'Never' },
    ...SENSIBLE_CRON_OPTIONS.map(([k]) => ({
      value: k,
      label: scheduleLabel(k),
    })),
  ];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <NewButton size="icon" variant="ghost">
          <Clock />
        </NewButton>
      </PopoverTrigger>
      <PopoverContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-2 w-full">
          <Combobox
            value={valueFromSchedule(when)}
            items={scheduleOptions}
            onChange={(v) => {
              if (STANDARD_SCHEDULES.includes(v as StandardSchedule)) {
                model.formState.set({
                  ...model.formState.snapshot(),
                  when: v as StandardSchedule,
                });
              } else {
                const value = CronExpression[v as keyof typeof CronExpression];
                if (value !== undefined) {
                  model.formState.set({
                    ...model.formState.snapshot(),
                    when: { cron: value },
                  });
                }
              }
            }}
          />
          <div className="text-sm text-muted-foreground">
            {scheduleDescription(when, 'agent', scheduleContext)}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const SliderWithInput: React.FC<{
  title: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  onChange: (value: number) => void;
  formatDisplay?: (value: number) => string;
}> = ({
  title,
  value,
  min,
  max,
  step = 0.01,
  onChange,
  formatDisplay = (v) => v.toString(),
}) => {
  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <span className="text-sm text-foreground/80">{title}</span>
        <Input
          type="number"
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={(e) => {
            const inputValue = Number(e.target.value);
            // Clamp the value between min and max
            const clampedValue = Math.min(Math.max(inputValue, min), max);
            onChange(clampedValue);
          }}
          className="w-24 text-xs"
        />
      </div>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={(v) => onChange(v[0] ?? min)}
        className="w-full"
      />
      <div className="flex justify-between text-xs text-muted-foreground">
        <span>{formatDisplay(min)}</span>
        <span>{formatDisplay(max)}</span>
      </div>
    </div>
  );
};

const ModelParametersPopover: React.FC = () => {
  const model = useColumnModel();
  const {
    model: modelId,
    temperature,
    maxOutputTokens,
  } = useWatchableValue(model.formState);
  const modelDetails = llm.getModel(modelId);

  if (!modelDetails) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <NewButton size="icon" variant="ghost">
          <SlidersHorizontal />
        </NewButton>
      </PopoverTrigger>
      <PopoverContent className="flex flex-col gap-4">
        <SliderWithInput
          title="Temperature"
          value={temperature}
          min={0}
          max={1}
          onChange={(v) => {
            model.formState.set({
              ...model.formState.snapshot(),
              temperature: v,
            });
          }}
        />
        <SliderWithInput
          title="Max Output Tokens"
          value={maxOutputTokens}
          min={1}
          max={modelDetails.maxOutputTokens}
          step={1}
          onChange={(v) => {
            model.formState.set({
              ...model.formState.snapshot(),
              maxOutputTokens: v,
            });
          }}
        />
        <DurableToggle
          title="Durable"
          description="This agent will run continuously and persist its state between runs."
        />
      </PopoverContent>
    </Popover>
  );
};

const DurableToggle: React.FC<{ title: string; description: string }> = ({
  title,
}) => {
  const model = useColumnModel();
  const { durable } = useWatchableValue(model.formState);

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm text-foreground/80">{title}</span>
      <Switch
        checked={durable}
        onCheckedChange={(v) => {
          model.formState.set({ ...model.formState.snapshot(), durable: v });
        }}
      />
    </div>
  );
};
const InstallToolsButton: React.FC = () => {
  const newToolAction = useAction(NewToolAction);

  return (
    <NewButton
      variant="ghost"
      onClick={() => newToolAction.execute()}
      title="Install Tools"
    >
      <Wrench className="h-4 w-4" />
      Install Tools
    </NewButton>
  );
};

export const Model: React.FC<{ showOutputPopover?: boolean }> = ({
  showOutputPopover = true,
}) => {
  const model = useColumnModel();
  const { model: modelId, when } = useWatchableValue(model.formState);
  const modelDetails = llm.getModel(modelId);
  const org = useWhoami((s) => s.org);
  const showModel = useGatedModelFilter();
  const eventStream = useWatchableValue(model.eventStream);

  // Current models by provider, then superseded versions under "Other
  // models", by vendor. The combobox lists sections in item order.
  const { current, legacyByVendor } = llm.groupLegacyModelsByVendor(
    llm
      .allowedModelsForOrg(
        org.id,
        Object.values(llm.MODELS).filter((m) => m.active)
      )
      .filter((m) => showModel(m))
  );
  const modelItems = [
    ...current.map((m) => ({ m, section: m.provider })),
    ...legacyByVendor.flatMap(({ vendor, models }) =>
      models.map((m) => ({ m, section: `Other models · ${vendor}` }))
    ),
  ].map(({ m, section }) => ({
    value: m.id,
    label: m.name,
    section,
    suffix: (
      <span className="flex items-center gap-1 text-muted-foreground text-xs ml-4">
        <Coins className="h-3 w-3 text-emerald-500" />
        {m.creditMultiplier}
      </span>
    ),
  }));

  // Get context from event stream (same logic as backend)
  const scheduleContext = eventStream
    ? getScheduleContextFromEventStream(org.id, eventStream)
    : undefined;

  return (
    <ConfigSection title="Model" padding={'default'}>
      <div className="flex flex-col gap-2 w-full">
        <div className="flex justify-between items-center">
          <div className="flex flex-col gap-1">
            <Combobox
              value={modelId}
              items={modelItems}
              onChange={(v) => {
                const newModelDetails = llm.getModel(v);
                const currentState = model.formState.snapshot();
                model.formState.set({
                  ...currentState,
                  model: v,
                  // Clamp maxOutputTokens to new model's max if current value exceeds it
                  maxOutputTokens:
                    newModelDetails &&
                    currentState.maxOutputTokens >
                      newModelDetails.maxOutputTokens
                      ? newModelDetails.maxOutputTokens
                      : currentState.maxOutputTokens,
                });
              }}
            />
            {modelDetails && (
              <span className="text-xs text-foreground/50">
                The selected model supports{' '}
                {Object.entries(modelDetails.supports)
                  .filter(([, supported]) => supported)
                  .map(([feature]) =>
                    feature.replace(/([A-Z])/g, ' $1').toLowerCase()
                  )
                  .join(', ')}
                .
              </span>
            )}
          </div>
          <div className="flex items-center">
            <InstallToolsButton />
            {showOutputPopover && <OutputPopover />}
            <SchedulePopover />
            <ModelParametersPopover />
          </div>
        </div>
        <span className="text-sm text-foreground/80">
          {scheduleDescription(when, 'agent', scheduleContext)}
        </span>
      </div>
    </ConfigSection>
  );
};
